export type ProductStatus = 'draft' | 'active' | 'stopped' | 'archived';

export interface ProductAuthor {
  id: string;
  name: string;
  description: string;
  photoUrl?: string;
}

export interface ProductTag {
  id: string;
  name: string;
  color: string;
  categoryId?: string;
  categoryName?: string;
}

export interface Product {
  id: string;
  /** К какой школе относится продукт. Картинки и часть настроек — на уровне школы. */
  schoolId?: string;
  name: string;
  url: string;
  description: string;
  tasks: string;
  keyIdea: string;
  keyMeanings: string;
  excludeWords: string;
  keywords: string;
  /** Глобальные минус-фразы на уровне продукта. Применяются ко всем кампаниям. */
  negativeKeywords?: string[];
  notes: string;
  comment: string;
  status: ProductStatus;
  authorId: string | null;
  author: ProductAuthor | null;
  tags: ProductTag[];
  offersCount: number;
  createdAt: string;
  createdBy: string;
}

export interface ProductPeriodStats {
  spend: number;
  spendChange: number;
  profit: number;
  profitChange: number;
  romi: number;
  romiChange: number;
  cpl: number;
  cplChange: number;
}

export interface ProductStats {
  activeCampaigns: number;
  activeAds: number;
  dailyBudget: number;
  periods: {
    '7d': ProductPeriodStats;
    '14d': ProductPeriodStats;
    '30d': ProductPeriodStats;
    all: ProductPeriodStats;
  };
}

export interface CreateProductFormValues {
  name: string;
  url: string;
}

// --- Offer types ---

export type PageGoal = string;

export interface OfferAddress {
  id: string;
  name: string;
  url: string;
  pageGoal: PageGoal;
  pageText: string;
}

export interface OfferRkBinding {
  id: string;
  rkId: string;
  rkName: string;
  rkSource: 'yandex_direct' | 'vk';
  rules: OfferBindingRule[];
  utmTemplate: UtmTemplate | null;
}

export interface OfferBindingRule {
  id: string;
  type: 'all' | 'contains' | 'campaign_id' | 'exact_name';
  value: string;
}

export interface UtmTemplate {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
}

export type OfferBindingScope = 'product' | 'sphere' | 'theme' | 'subtheme';

export interface OfferBinding {
  scope: OfferBindingScope;
  scopeId: string;
}

export interface Offer {
  id: string;
  productId: string;
  name: string;
  description?: string;
  bindings?: OfferBinding[];
  keyPromise?: string;
  averageCheck?: number | null;
  address?: OfferAddress;
  metrikaConversionId?: string;
  desiredConversionCost?: number | null;
  rkBindings?: OfferRkBinding[];
  tags?: ProductTag[];
  createdAt?: string;
}

export interface CreateOfferFormValues {
  name: string;
  keyPromise: string;
  averageCheck: number | null;
  addressName: string;
  addressUrl: string;
  addressPageGoal: string;
  metrikaConversionId: string;
  desiredConversionCost: number | null;
}

// --- Semantic map types ---

/**
 * Иерархия после миграции май-2026:
 * Сфера → Тема → Подтема → Смысловая единица → Заход → Объявление
 * Решения зафиксированы в memory/project_ad_system_decisions.md.
 */

/** Статус узла иерархии. Применяется к Sphere/Theme/Subtheme/SemanticUnit/Angle. */
export type NodeStatus = 'draft' | 'approved' | 'testing' | 'running' | 'disabled';

export const NODE_STATUS_CONFIG: Record<NodeStatus, { label: string; color: string }> = {
  draft: { label: 'Черновик', color: 'default' },
  approved: { label: 'Утверждено', color: 'blue' },
  testing: { label: 'В тесте', color: 'gold' },
  running: { label: 'Работает', color: 'green' },
  disabled: { label: 'Отключено', color: 'red' },
};

/** Запись в истории изменений узла. */
export interface NodeHistoryEntry {
  id: string;
  at: string;
  by: string;
  /** Тип события: смена статуса, переименование, ручная правка, авто-действие, … */
  kind: 'status' | 'rename' | 'edit' | 'auto' | 'create' | 'delete';
  message: string;
  /** Произвольные данные (например, { from: 'draft', to: 'approved' }). */
  payload?: Record<string, unknown>;
}

export interface SemanticUnit {
  id: string;
  subthemeId: string;
  name: string;
  description: string;
  examples: string[];
  keywords?: string[];
  negativeKeywords?: string[];
  /** Статус. Default: 'draft'. */
  status?: NodeStatus;
  /** Приоритет/«горячесть» 1..10. Чем выше — тем раньше попадает в авто-запуск. */
  priority?: number;
  history?: NodeHistoryEntry[];
}

/** Подтема (бывш. Theme). */
export interface Subtheme {
  id: string;
  themeId: string;
  name: string;
  description: string;
  units: SemanticUnit[];
  keywords?: string[];
  negativeKeywords?: string[];
  status?: NodeStatus;
  priority?: number;
  history?: NodeHistoryEntry[];
}

/** Тема (бывш. ThemeCategory). */
export interface Theme {
  id: string;
  sphereId: string;
  name: string;
  description: string;
  subthemes: Subtheme[];
  keywords?: string[];
  /** @deprecated Картинки переезжают в библиотеку школы (SchoolImage). Поле сохраняется для миграции. */
  images?: string[];
  status?: NodeStatus;
  priority?: number;
  history?: NodeHistoryEntry[];
}

export interface LifeSphere {
  id: string;
  productId: string;
  name: string;
  description: string;
  themes: Theme[];
  status?: NodeStatus;
  priority?: number;
  history?: NodeHistoryEntry[];
}

// --- Angle types ---

export type AngleType =
  | 'rational'
  | 'expert'
  | 'social_proof'
  | 'pain'
  | 'dream'
  | 'secret'
  | 'instruction'
  | 'urgency'
  | 'contrast'
  | 'provocation'
  | 'question'
  | 'story'
  | 'technical'
  | 'humor'
  | 'elite'
  | 'esoteric'
  | 'scientific'
  | 'revolutionary'
  | 'gamification'
  | 'consequences'
  | 'supportive'
  | 'metamorphic'
  | 'visionary'
  | 'community';

export interface AngleTypeInfo {
  label: string;
  description: string;
  example: string;
  howItWorks?: string;
  whenToUse?: string;
  whenNotToUse?: string;
}

export const ANGLE_TYPE_CONFIG: Record<AngleType, AngleTypeInfo> = {
  rational: { label: 'Рациональный', description: 'Логические аргументы, факты, цифры, сравнения. Для аудитории, которая принимает решения головой.', example: '3 месяца × 20 минут в день = минус 12 кг. Математика, не магия.', howItWorks: 'Снижает сопротивление через логику: цифры, расчёты, сравнения. Аудитория сама приходит к выводу.', whenToUse: 'Сложный/дорогой продукт, B2B, аналитическая аудитория, мужская ЦА, эксперты в теме.', whenNotToUse: 'Эмоциональная/импульсная покупка, широкая массовая ЦА, низкая осознанность проблемы.' },
  expert: { label: 'Экспертный', description: 'Авторитет, регалии, опыт. Ссылка на конкретного специалиста, его стаж и профессиональный опыт.', example: 'Врач-диетолог с 15-летним стажем раскрывает систему питания.', howItWorks: 'Передаёт доверие от авторитета к продукту. «Он разобрался — я могу положиться».', whenToUse: 'Есть сильный эксперт-лицо, сложная тема (здоровье, юр., финансы), аудитория 30+.', whenNotToUse: 'Нет реальных регалий, молодая массовая ЦА, развлекательный продукт.' },
  social_proof: { label: 'Соц. доказательство', description: 'Отзывы, кейсы, статистика, количество клиентов. Доказательство через массовый опыт других людей.', example: '89% участниц похудели без возврата веса. 4 700 отзывов.', howItWorks: 'Срабатывает стадный инстинкт: «раз другим помогло — поможет и мне». Снижает риск.', whenToUse: 'Много отзывов/кейсов, массовый продукт, новичковая аудитория без опыта.', whenNotToUse: 'Мало данных, премиум-сегмент (там важнее эксклюзивность), нишевая B2B.' },
  pain: { label: 'Болевой', description: 'Давление на текущую боль, страх, дискомфорт. Показывает что человек переживает прямо сейчас и почему это невыносимо.', example: 'Каждый день без изменений — ещё один день, когда вы ненавидите отражение.', howItWorks: 'Активирует уже существующую боль, повышает её остроту → толкает искать решение.', whenToUse: 'Острая проблема (здоровье, отношения, деньги), ЦА осознаёт боль, но откладывает.', whenNotToUse: 'Холодная ЦА без проблемы, радостный/подарочный продукт, депрессивная аудитория.' },
  consequences: { label: 'Последствия бездействия', description: 'Показывает что произойдёт, если ничего не менять. Давление через картину ухудшения в будущем.', example: 'Через 5 лет без изменений — диабет, давление, горсть таблеток каждое утро.', howItWorks: 'Проецирует текущее состояние в будущее, делает цену бездействия конкретной и страшной.', whenToUse: 'Хроническая проблема, прокрастинирующая ЦА, здоровье/финансы/обучение.', whenNotToUse: 'Ситуативная проблема «на один раз», молодая ЦА без горизонта планирования.' },
  dream: { label: 'Мечта / Результат', description: 'Рисует конкретную картину идеального будущего. Визуализация результата — как будет выглядеть жизнь после.', example: 'Представьте: вы на пляже, в любимом бикини, без единой мысли о диете.', howItWorks: 'Активирует дофамин через воображение награды — продукт становится путём к мечте.', whenToUse: 'Трансформационный продукт, эмоциональная ЦА, женская аудитория, обучение/путешествия.', whenNotToUse: 'Рациональная B2B-покупка, технический продукт, скептическая ЦА.' },
  supportive: { label: 'Поддерживающий', description: 'Даёт опору и веру в процессе. «Ты не одна, у тебя получится, шаг за шагом». Мягкий, тёплый тон поддержки.', example: 'У вас получится. Тысячи женщин уже прошли этот путь — и вы сможете.', howItWorks: 'Снимает страх провала, даёт ощущение, что не будешь один в процессе.', whenToUse: 'Долгий путь трансформации, ЦА с неудачным опытом, уязвимые темы.', whenNotToUse: 'Быстрое решение, рациональная B2B-ЦА, агрессивный маркетинг.' },
  secret: { label: 'Секрет / Интрига', description: 'Создаёт любопытство через недосказанность, секрет, неожиданный факт. «Я знаю то, чего ты не знаешь».', example: 'Один продукт, который нутрициологи скрывают от вас. И это не суперфуд.', howItWorks: 'Создаёт информационный разрыв (curiosity gap) — мозг не терпит незакрытого вопроса.', whenToUse: 'Низкий CTR на стандартные объявления, холодная ЦА, контентные воронки.', whenNotToUse: 'Премиум-сегмент (кликбейт снижает доверие), тема с высоким скептицизмом.' },
  revolutionary: { label: 'Революционный', description: 'Прорыв, новый метод, переворот в подходе. «Старое не работает — вот что пришло на замену».', example: 'Новый метод, который перечёркивает всё, что вы знали о похудении.', howItWorks: 'Обесценивает старый опыт («ты пробовал не то») и предлагает новый путь.', whenToUse: 'ЦА с опытом неудачных попыток, уникальная методика, инновационный продукт.', whenNotToUse: 'Стандартный/проверенный продукт, консервативная аудитория, отсутствие реальной новизны.' },
  visionary: { label: 'Будущего / Трендовый', description: 'Позиционирование через тренды и взгляд в будущее. «Мир меняется — будь впереди, а не позади».', example: 'К 2030 году персональное питание заменит диеты. Начните сейчас.', howItWorks: 'Давит на страх остаться позади + амбиция быть в авангарде.', whenToUse: 'Тех-продукт, новая ниша, молодая амбициозная ЦА, B2B-инновации.', whenNotToUse: 'Классические традиционные продукты, консервативная аудитория.' },
  instruction: { label: 'Инструктивный', description: 'Пошаговый план, how-to, конкретные действия. Обещает структуру и ясность: «делай раз, два, три».', example: '5 шагов к правильному питанию — без подсчёта калорий и срывов.', howItWorks: 'Снижает тревогу «не знаю с чего начать» — конкретика ощущается как контроль.', whenToUse: 'Сложная тема, ЦА-новички, образовательный продукт, чек-листы/гайды.', whenNotToUse: 'Продукт вдохновения/эмоций, опытная аудитория, премиум.' },
  urgency: { label: 'Срочность / Дефицит', description: 'Ограничение по времени, количеству мест, скидке. Создаёт давление через страх упустить возможность.', example: 'Осталось 7 мест на вебинар. Набор закрывается в пятницу.', howItWorks: 'FOMO — страх упустить. Толкает к быстрому решению, минуя долгие сомнения.', whenToUse: 'Последний этап воронки, ретаргетинг, горячая ЦА, реальный дедлайн/ограничение.', whenNotToUse: 'Холодная ЦА (ещё не решила о покупке), выдуманный дефицит (быстро раскусывают).' },
  contrast: { label: 'Контрастный', description: 'Сравнение до/после, было/стало. Моментальный снимок двух состояний — показывает трансформацию.', example: 'Было: заедаю стресс каждый вечер. Стало: наслаждаюсь едой без чувства вины.', howItWorks: 'Две картины рядом → мозг видит «разницу» = ценность продукта становится наглядной.', whenToUse: 'Трансформационный продукт, есть реальные кейсы, визуальные результаты.', whenNotToUse: 'Неявные/медленные изменения, интеллектуальные/концептуальные продукты.' },
  provocation: { label: 'Провокационный', description: 'Ломает шаблон, вызывает реакцию. Резкие утверждения, вызов устоявшимся убеждениям, парадокс.', example: 'Диеты не работают. Хватит себя мучить ради цифры на весах.', howItWorks: 'Парадокс останавливает внимание, ломает автопилот скролла.', whenToUse: 'Перегретая ниша с одинаковыми офферами, молодая ЦА, сильный эксперт/бренд.', whenNotToUse: 'Консервативная ЦА, чувствительная тема (можно оскорбить), слабый бренд без доверия.' },
  question: { label: 'Вопросительный', description: 'Вовлекает через вопрос — от простого до глубокого философского. Мозг автоматически ищет ответ.', example: 'А что если всё, что вы знали о похудении — неправда?', howItWorks: 'Открытая петля — мозг включается в поиск ответа, внимание задерживается.', whenToUse: 'Квизы, диагностические воронки, контентная прогрева.', whenNotToUse: 'Нужен прямой CTA, низкий интерес к размышлениям, быстрые продажи.' },
  story: { label: 'Историйный / Кейс', description: 'Нарратив с конкретным персонажем и сюжетом. Читатель идентифицирует себя с героем истории.', example: 'Марина 3 года боролась с весом. А потом узнала одну вещь...', howItWorks: 'История обходит критическое мышление, даёт идентификацию и эмоциональный отклик.', whenToUse: 'Есть сильный кейс клиента, длинная воронка, обучение/коучинг.', whenNotToUse: 'Короткие форматы (заголовок не помещает историю), рациональная B2B-продажа.' },
  technical: { label: 'Технический', description: 'Фокус на характеристиках продукта: модули, часы, формат, инструменты. Факты без эмоций.', example: '12 модулей, 48 уроков, 6 практических заданий. PDF-гайд и чек-листы.', howItWorks: 'Аудитория оценивает соотношение «цена / содержимое», даёт ощущение честности.', whenToUse: 'Понимающая продукт ЦА, курс/сервис с богатым содержимым, финальный этап воронки.', whenNotToUse: 'Холодная ЦА (ещё не нужны детали), эмоциональный продукт, low-touch товары.' },
  humor: { label: 'Юмор', description: 'Шутки, ирония, лёгкий тон. Снижает защитные барьеры, запоминается, распространяется вирально.', example: 'Диета — это когда холодильник плачет ночью вместе с вами.', howItWorks: 'Юмор → позитивная эмоция → бренд ассоциируется с радостью + выше запоминаемость.', whenToUse: 'Молодая ЦА, соцсети, контент-маркетинг, перегретая ниша.', whenNotToUse: 'Серьёзные темы (медицина, утраты), премиум-сегмент, B2B-сделки.' },
  elite: { label: 'Элитный / Премиум', description: 'Позиционирование через эксклюзивность: VIP, закрытый клуб, ограниченный доступ, персональное внимание.', example: 'Закрытый клуб нутрициологии. Только 30 участниц. Персональный куратор.', howItWorks: 'Статус + дефицит места. Ценность через недоступность для большинства.', whenToUse: 'Высокий средний чек (от 100к ₽), зрелая платёжеспособная ЦА.', whenNotToUse: 'Дешёвый продукт, широкая ЦА, эконом-сегмент, демократичное позиционирование.' },
  esoteric: { label: 'Эзотерический', description: 'Энергия, вибрации, внутренний мир, духовные практики. Для аудитории, мыслящей категориями внутренней работы.', example: 'Ваше тело — отражение внутреннего состояния. Начните изнутри.', howItWorks: 'Резонирует с языком «внутренней работы» — своя ЦА узнаёт «своего».', whenToUse: 'ЦА в теме психологии/эзотерики/духовных практик, курсы трансформации.', whenNotToUse: 'Рациональная/научная аудитория, B2B, широкая массовая ЦА.' },
  scientific: { label: 'Научный', description: 'Ссылки на исследования, научные термины, доказательная база. Доверие через авторитет науки.', example: 'Исследование Nature (2024): интервальное голодание снижает воспаление на 47%.', howItWorks: 'Доверие к науке → трансфер доверия продукту. Снижает риск «это обман».', whenToUse: 'Медицина/психология/финансы, образованная ЦА, реальные исследования в основе.', whenNotToUse: 'Нет реальной доказательной базы (раскусят), эмоциональная ЦА, развлекательный продукт.' },
  gamification: { label: 'Игровой', description: 'Челлендж, квиз, тест, баллы, соревнование. Вовлекает через интерактивную игровую механику.', example: 'Пройдите тест: какой тип метаболизма у вас? 7 вопросов — и ваш план готов.', howItWorks: 'Интерактив включает азарт + персонализация результата даёт ощущение релевантности.', whenToUse: 'Квиз-воронки, холодная ЦА, лид-магниты, сбор сегментов.', whenNotToUse: 'Серьёзная B2B-сделка, высокий чек требует глубокой проработки доверия.' },
  metamorphic: { label: 'Метаморфозный', description: 'Яркие образы, метафоры, сравнения. Превращает абстрактное в наглядное через образ-посредник.', example: 'Ваше тело — не тюрьма. Это дом, который можно перестроить.', howItWorks: 'Метафора делает абстракцию осязаемой, создаёт эмоциональную картинку.', whenToUse: 'Концептуальные/неочевидные продукты, коучинг/психотерапия, контентная прогрева.', whenNotToUse: 'Конкретный/простой продукт, последний этап воронки (нужна конкретика).' },
  community: { label: 'Комьюнити', description: 'Сообщество, коллектив, «мы вместе». Чувство принадлежности к группе единомышленников.', example: 'Присоединяйтесь к 4 700 женщинам, которые меняют питание вместе.', howItWorks: 'Удовлетворяет потребность принадлежности, снимает ощущение одиночества в пути.', whenToUse: 'Одинокая ЦА, долгий трансформационный путь, курсы/марафоны с чатами.', whenNotToUse: 'Индивидуалистичная B2B-ЦА, одноразовый продукт без длительного процесса.' },
};

/**
 * Заход (угол захода / стиль).
 *
 * После миграции это полноценный уровень иерархии (между СЕ и объявлением),
 * а не просто массив на СЕ. У захода свой статус и приоритет —
 * можно отключить конкретный «угол» по СЕ.
 */
export interface Angle {
  id: string;
  unitId: string;
  type: AngleType;
  enabled: boolean;
  /** Оценка применимости для текущего продукта/оффера (0-100) */
  score: number;
  /** Обоснование — почему этот стиль подходит или не подходит */
  reasoning: string;
  status?: NodeStatus;
  priority?: number;
  history?: NodeHistoryEntry[];
}

// --- Ad cabinet types ---

export interface AdCabinet {
  id: string;
  productId: string;
  platform: AdPlatform;
  name: string;
  externalId: string;
}

// --- Ads types ---

export type AdPlatform = 'yandex_direct' | 'vk';

export type AdStatus = 'active' | 'paused' | 'draft';

/** @deprecated Используйте Offer. Alias временно оставлен до миграции. */
export type SmOffer = Offer;

export interface Ad {
  id: string;
  angleId: string;
  offerId: string;
  platform: AdPlatform;
  cabinetId: string;
  title: string;
  title2: string;
  text: string;
  url: string;
  displayUrl: string;
  sitelinks: [AdSitelink, AdSitelink, AdSitelink, AdSitelink];
  callouts: string[];
  actionButton: string;
  status: AdStatus;
  /** Base64 или URL изображения для объявления */
  image?: string;
  /** Текст на картинку */
  imageText: string;
  /** Текст кнопки на картинку */
  imageButtonText: string;
  /** Настройки наложения текста и кнопки на изображение */
  imageSettings?: AdImageSettings;
}

export interface AdImageSettings {
  // Текст на изображении
  overlayText: string;
  textColor: string;
  textShadow: boolean;
  textShadowX: number;
  textShadowY: number;
  textShadowBlur: number;
  textShadowColor: string;
  textShadowOpacity: number;
  fontFamily: string;
  // Кнопка на изображении
  buttonText: string;
  buttonColor: string;
  buttonTextColor: string;
  buttonBorderRadius: number;
  buttonShadow: boolean;
  buttonShadowX: number;
  buttonShadowY: number;
  buttonShadowBlur: number;
  buttonShadowColor: string;
  buttonShadowOpacity: number;
  // Расположение
  bottomMargin: number;
  sideMargin: number;
  // Градиент
  gradientEnabled: boolean;
  gradientTopColor: string;
  gradientTopOpacity: number;
  gradientBottomColor: string;
  gradientBottomOpacity: number;
  // Полная заливка
  overlayColor: string;
  overlayOpacity: number;
}

export const DEFAULT_AD_IMAGE_SETTINGS: AdImageSettings = {
  overlayText: '',
  textColor: '#ffffff',
  textShadow: true,
  textShadowX: 3,
  textShadowY: 3,
  textShadowBlur: 8,
  textShadowColor: '#000000',
  textShadowOpacity: 100,
  fontFamily: 'Arial',
  buttonText: '',
  buttonColor: '#3b82f6',
  buttonTextColor: '#ffffff',
  buttonBorderRadius: 8,
  buttonShadow: false,
  buttonShadowX: 0,
  buttonShadowY: 6,
  buttonShadowBlur: 12,
  buttonShadowColor: '#000000',
  buttonShadowOpacity: 100,
  bottomMargin: 13,
  sideMargin: 20,
  gradientEnabled: true,
  gradientTopColor: '#000000',
  gradientTopOpacity: 30,
  gradientBottomColor: '#000000',
  gradientBottomOpacity: 70,
  overlayColor: '#000000',
  overlayOpacity: 0,
};

export interface AdSitelink {
  title: string;
  description: string;
  url: string;
}

// --- Generation settings ---

/**
 * Лимиты полей Яндекс Директ (текстово-графические объявления, ЕПК).
 * Источник: https://yandex.ru/support/direct/ru/moderation/technical-restrictions
 */
export interface YandexDirectFieldLimits {
  /** Заголовок 1: макс. 56 символов (с пробелами), одно слово макс. 22 символа */
  title1MaxChars: number;
  title1MaxWordChars: number;
  /** Доп. заголовок: макс. 30 символов + до 15 знаков препинания, одно слово макс. 22 */
  title2MaxChars: number;
  title2MaxPunctuation: number;
  title2MaxWordChars: number;
  /** Текст: макс. 81 символ + до 15 знаков препинания, одно слово макс. 23 */
  textMaxChars: number;
  textMaxPunctuation: number;
  textMaxWordChars: number;
  /** Отображаемая ссылка: макс. 20 символов (без домена) */
  displayUrlMaxChars: number;
  /** Быстрая ссылка — заголовок: макс. 30 символов */
  sitelinkTextMaxChars: number;
  /** Быстрая ссылка — описание: макс. 60 символов */
  sitelinkDescMaxChars: number;
  /** Макс. количество быстрых ссылок */
  sitelinksCount: number;
  /** Уточнение: макс. 25 символов; общая длина ≤132 (ПК), ≤66 (моб.) */
  calloutMaxChars: number;
  calloutsTotalMaxCharsDesktop: number;
  calloutsTotalMaxCharsMobile: number;
  /** Макс. количество уточнений */
  calloutsCount: number;
}

export interface GenerationRule {
  id: string;
  text: string;
  enabled: boolean;
}

export interface YandexDirectGenerationSettings {
  fieldLimits: YandexDirectFieldLimits;
  rules: GenerationRule[];
  generateTitle2: boolean;
  generateDisplayUrl: boolean;
  generateSitelinks: boolean;
  generateSitelinkDesc: boolean;
  generateCallouts: boolean;
}

export const DEFAULT_YANDEX_DIRECT_LIMITS: YandexDirectFieldLimits = {
  title1MaxChars: 56,
  title1MaxWordChars: 22,
  title2MaxChars: 30,
  title2MaxPunctuation: 15,
  title2MaxWordChars: 22,
  textMaxChars: 81,
  textMaxPunctuation: 15,
  textMaxWordChars: 23,
  displayUrlMaxChars: 20,
  sitelinkTextMaxChars: 30,
  sitelinkDescMaxChars: 60,
  sitelinksCount: 8,
  calloutMaxChars: 25,
  calloutsTotalMaxCharsDesktop: 132,
  calloutsTotalMaxCharsMobile: 66,
  calloutsCount: 4,
};

export const DEFAULT_YANDEX_DIRECT_RULES: GenerationRule[] = [
  { id: 'r1', text: 'Заголовок 1 должен содержать ключевую фразу из смысловой единицы', enabled: true },
  { id: 'r2', text: 'Заголовок 2 усиливает первый — выгода, цифра или дедлайн', enabled: true },
  { id: 'r3', text: 'Текст объявления должен содержать призыв к действию (CTA)', enabled: true },
  { id: 'r4', text: 'Использовать конкретные цифры и факты, не абстрактные обещания', enabled: true },
  { id: 'r5', text: 'Не использовать капслок и чрезмерные восклицательные знаки', enabled: true },
  { id: 'r6', text: 'Одно слово в заголовке не длиннее 22 символов, в тексте — 23', enabled: true },
  { id: 'r7', text: 'Быстрые ссылки ведут на разные разделы (программа, цена, отзывы, кейсы)', enabled: true },
  { id: 'r8', text: 'Уточнения — короткие факты-выгоды (диплом, рассрочка, гарантия)', enabled: true },
  { id: 'r9', text: 'Текст должен соответствовать выбранному углу захода', enabled: true },
  { id: 'r10', text: 'Знаков препинания не более 15 в доп. заголовке и тексте', enabled: true },
];

// --- Campaign types ---

export type CampaignStatus = 'draft' | 'active' | 'paused' | 'archived';

export type CampaignPlacement = 'search' | 'networks' | 'both';

export type CampaignPaymentModel = 'per_conversions' | 'per_clicks';

export type GroupingLevel = 'sphere' | 'theme' | 'subtheme';

/** Тип группы объявлений в кампании Я.Директа. */
export type AdGroupType = 'keywords' | 'auto_targeting' | 'interests';

export const AD_GROUP_TYPE_CONFIG: Record<AdGroupType, string> = {
  keywords: 'Ключевые слова',
  auto_targeting: 'Авто-таргетинг',
  interests: 'Интересы',
};

export interface CampaignSettings {
  groupingLevel: GroupingLevel;
  selectedItemIds: string[];
  adsPerGroup: number;
  selectedAngles: AngleType[];
  /**
   * Какие типы групп объявлений создавать в этой кампании.
   * Можно несколько (например, ['keywords', 'auto_targeting']).
   * Default при создании: ['keywords'].
   */
  groupTypes?: AdGroupType[];
}

export interface Campaign {
  id: string;
  productId: string;
  name: string;
  platform: AdPlatform;
  cabinetId: string;
  offerId: string;
  placement: CampaignPlacement;
  strategy: string;
  paymentModel: CampaignPaymentModel;
  weeklyBudget: number;
  conversionPriceLimit: number | null;
  conversionName: string;
  negativeKeywords: string[];
  settings: CampaignSettings;
  status: CampaignStatus;
  createdAt: string;
  createdBy: string;
  clonedFromId: string | null;
}

export interface AdGroup {
  id: string;
  campaignId: string;
  sourceId: string;
  sourceLevel: GroupingLevel;
  name: string;
  keywords: string[];
  suKeywords: string[];
  images: string[];
  enabled: boolean;
  /** Тип группы (keywords / auto_targeting / interests). Default: 'keywords'. */
  type?: AdGroupType;
}

export interface CampaignAd {
  id: string;
  adGroupId: string;
  campaignId: string;
  unitId: string;
  angleType: AngleType;
  name?: string;
  title: string;
  title2: string;
  text: string;
  url: string;
  displayUrl: string;
  sitelinks: [AdSitelink, AdSitelink, AdSitelink, AdSitelink];
  callouts: string[];
  actionButton: string;
  image?: string;
  imageText: string;
  imageButtonText: string;
  imageSettings?: AdImageSettings;
  status: AdStatus;
  variantIndex: number;
}

export const CAMPAIGN_STATUS_CONFIG: Record<CampaignStatus, { label: string; color: string }> = {
  draft: { label: 'Черновик', color: 'default' },
  active: { label: 'Активна', color: 'green' },
  paused: { label: 'Пауза', color: 'orange' },
  archived: { label: 'Архив', color: 'red' },
};

export const PLACEMENT_CONFIG: Record<CampaignPlacement, string> = {
  search: 'Поиск',
  networks: 'Сети',
  both: 'Поиск и Сети',
};

export const PAYMENT_MODEL_CONFIG: Record<CampaignPaymentModel, string> = {
  per_conversions: 'За конверсии',
  per_clicks: 'За клики',
};

export const GROUPING_LEVEL_CONFIG: Record<GroupingLevel, string> = {
  sphere: 'По сферам',
  theme: 'По темам',
  subtheme: 'По подтемам',
};

// --- School image library ---

/**
 * Картинка в библиотеке школы. Картинки шарятся между всеми продуктами одной школы,
 * формат 4:3 после обрезки.
 */
export interface SchoolImage {
  id: string;
  schoolId: string;
  /** Data URL или ссылка. */
  url: string;
  /** Имя файла или название картинки. */
  name: string;
  /** Кропнутая область в исходнике (px), null если использовалась как есть. */
  crop?: { x: number; y: number; width: number; height: number } | null;
  /** Метки для фильтрации (например, ['fitness', 'женщина', 'улыбка']). */
  tags?: string[];
  createdAt: string;
}

// --- Auto mode (рамки для автоматического режима) ---

/**
 * Настройки авто-режима, который пользователь задаёт перед запуском.
 * Система внутри этих рамок генерирует и масштабирует кампании.
 */
export interface AutoModeSettings {
  id: string;
  productId: string;
  /** Сколько готов тратить в день суммарно (₽). */
  dailyBudget: number;
  /** Жёсткий потолок недельного бюджета на одну кампанию (₽). */
  maxBudgetPerCampaign: number;
  /** Жёсткий потолок суммарного бюджета (₽). */
  maxBudgetTotal: number;
  /** Желаемая стоимость конверсии (₽). */
  targetCpa?: number;
  /** Длительность тестового периода кампании, дней. */
  testDurationDays: number;
  /** Сколько СЕ из топа приоритизации запускать на старте. */
  topNUnits: number;
  /** Платформы, на которых работаем. */
  platforms: AdPlatform[];
  /** Кабинеты, в которых запускаем (привязка к platform). */
  cabinetIds: string[];
  /** Критерии успеха теста. */
  successCriteria?: {
    minConversions?: number;
    maxCpa?: number;
    minRomi?: number;
  };
  /** Критерии провала теста. */
  failCriteria?: {
    zeroConversionsDays?: number;
    maxCpa?: number;
    maxCpc?: number;
  };
  enabled: boolean;
  createdAt: string;
  updatedAt?: string;
}
